import { isImageEmpty, isImageReady } from '@/ui/editor/image-readiness'
import { campaignsShowQueryKey, mediaImageIndexQueryKey, useCampaignsShow, useFeedsUpdate } from '@jobilla/nexus-sdk'
import { useAtomValue } from 'jotai'
import { useEffect } from 'react'
import { queryClient } from '../../main'
import { activeFeedChatToolAtom, ConformedCampaignFeedAd, conformFeedAd, useFeedEditor, usePrevious } from '../../ui'

export function useNewFeedEditor(campaignId: string) {
	const campaign = useCampaignsShow(campaignId)

	const feed = campaign.data?.feed

	const feedAd: ConformedCampaignFeedAd | undefined = feed && feed.id
		? conformFeedAd({
			id: feed.id,
			title: feed.title ?? '',
			subtitle: feed.subtitle ?? '',
			location: feed.location ?? '',
			primaryText: feed.metaPrimaryText ?? '',
			headlineText: feed.metaTitle ?? '',
			descriptionText: feed.metaDescription ?? '',
			ctaText: feed.button ?? '',
			metaCtaText: feed.metaCallToAction ?? '',
			imageId: feed.imageId ?? '',
			meta: feed.meta?.__ui ?? {},
		})
		: undefined

	const imagesHistory = feed?.imagesHistory?.filter((image) => !isImageEmpty(image)).map((image) => ({
		id: image.id,
		category: 'image' as const,
		collection: image.collectionName,
		name: image.name,
		fileName: image.fileName,
		humanReadableSize: image.humanReadableSize,
		createdAt: new Date(image.createdAt),
		url: image.url,
		width: image.width,
		height: image.height,
		isReady: isImageReady(image),
	}))

	const image = feed?.image
		? {
			id: feed.image.id,
			url: feed.image.url,
			isReady: isImageReady(feed.image),
			isEmpty: isImageEmpty(feed.image),
		}
		: undefined

	const feedMutation = useFeedsUpdate({
		mutationKey: ['update-feed'],
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: campaignsShowQueryKey(campaignId),
			})
		},
	})

	const handleFeedAdChange = async (value: ConformedCampaignFeedAd) => {
		if (!feed) {
			throw new Error('Feed not found')
		}
		if (!feed.id) {
			throw new Error('Feed ID missing')
		}

		await feedMutation.mutateAsync({
			feed: feed.id,
			data: {
				campaignId,

				title: value.title,
				subtitle: value.subtitle,
				location: value.location,
				button: value.ctaText,
				metaPrimaryText: value.primaryText,
				metaTitle: value.headlineText,
				metaDescription: value.descriptionText,
				metaCallToAction: value.metaCtaText,
				imageId: value.imageId,

				meta: {
					...(feed.meta || {}),
					__ui: value.meta,
				},
			},
		})
	}

	const isToolExecuting = useAtomValue(activeFeedChatToolAtom)

	const { lifecycle } = useFeedEditor({
		feedAd,
		isPending: campaign.isPending,
		isGenerating: feed?.isGenerated !== true,
		isToolExecuting,
		imagesHistory,
		image,
		onFeedAdChange: handleFeedAdChange,
		feedAdVersion: feed?.updatedAt,
	})

	// Invalidate media queries when transitioning to 'ready' state
	const prevLifecycle = usePrevious(lifecycle)
	useEffect(() => {
		if (lifecycle === 'ready' && prevLifecycle !== 'ready') {
			void queryClient.invalidateQueries({
				queryKey: mediaImageIndexQueryKey(),
			})
		}
	}, [lifecycle, prevLifecycle])

	return {
		isSaving: feedMutation.isPending,
		isGenerating: feed?.isGenerated !== true,
	}
}
